import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Autosuggest from 'react-autosuggest'
import AutosuggestHighlightMatch from 'autosuggest-highlight/match'
import AutosuggestHighlightParse from 'autosuggest-highlight/parse'

import useTypeahead, { spinnerInput } from '@/hooks/useTypeahead'

const UserFollowerAutocomplete = ({ addUser, placeholder }) => {
  const { get, cancel, loading } = useTypeahead()
  const [users, setUsers] = useState([])
  const [searchText, setSearchText] = useState('')

  const onKeyDown = (event) => {
    const { keyCode } = event
    if (keyCode === 13 || keyCode === 188) {
      // we want to do nothing here. do not allow them to type in any user
    }
  }

  const fetchData = (newValue) => {
    get('/api/v3/omnisearch', { search: newValue }, (err, jsonData) => {
      if (err) { /* hook */ return }
      setUsers(jsonData.users)
    }, { now: newValue === '' })
  }

  const handleAutocomplete = (event, { newValue, method }) => {
    if (method === 'up' || method === 'down') return

    setSearchText(newValue)

    if (method === 'enter' || method === 'click') {
      cancel()
      addUser(newValue)
      setSearchText('')
      return
    }

    fetchData(newValue)
  }

  // When suggestion is clicked, Autosuggest needs to populate the input
  // based on the clicked suggestion. Teach Autosuggest how to calculate the
  // input value for every given suggestion.
  const getSuggestionValue = suggestion => suggestion ? suggestion.autosuggest : ''

  const renderSuggestion = (suggestion, { query, isHighlighted }) => {
    const matches = AutosuggestHighlightMatch(suggestion.username, query)
    const parts = AutosuggestHighlightParse(suggestion.username, matches)
    return (
      <div className={`${isHighlighted ? 'bg-cccpurple bg-opacity-20 text-gray-900' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200'} w-full h-full p-2 cursor-pointer capitalize`}>
        {parts.map((part, index) => {
          const className = part.highlight ? 'text-cccblue font-semibold' : null

          return (
            <span className={className} key={index}>
              {part.text}
            </span>
          )
        })}
      </div>
    )
  }

  // Autosuggest will call this function every time you need to update suggestions.
  // You already implemented this logic above, so just use it.
  const onSuggestionsFetchRequested = ({ value }) => { /* setFranchises(value) */ }
  const onSuggestionsClearRequested = () => { setUsers([]) }
  const shouldRenderSuggestions = (value, reason) => {
    return value.trim().length >= 0
  }

  const onFocus = (event) => {
    if (users.length === 0) {
      setSearchText('')
      fetchData('')
    }
  }

  const inputProps = {
    placeholder: placeholder,
    value: searchText, // must be a string
    type: 'search',
    onKeyDown: onKeyDown,
    onFocus: onFocus,
    onChange: handleAutocomplete
  }

  return <Autosuggest
    className=''
    suggestions={users}
    highlightFirstSuggestion={true}
    onSuggestionsFetchRequested={onSuggestionsFetchRequested}
    onSuggestionsClearRequested={onSuggestionsClearRequested}
    shouldRenderSuggestions={shouldRenderSuggestions}
    getSuggestionValue={getSuggestionValue}
    renderSuggestion={renderSuggestion}
    renderInputComponent={spinnerInput(loading)}
    inputProps={inputProps}
  />
}

export default UserFollowerAutocomplete

UserFollowerAutocomplete.propTypes = {
  placeholder: PropTypes.string,
  addUser: PropTypes.func.isRequired
}
